/**
 * 파일 첨부 한도 (Single Source of Truth) — Frontend
 *
 * backend/config/file_limits.py 와 동기화된 프론트엔드 파일 한도 모듈.
 *
 * 파일 첨부가 허용되는 모든 경로에 적용할 한도를 한 곳에서 관리한다.
 *
 * Pro 판별 표준:
 * - SubscriptionContext 의 `subscriptionStatus.isPro` 또는
 *   `useSubscription().isProUser()` 를 그대로 사용한다.
 * - is_pro = subscription.type.startswith('pro') AND status === 'active'
 * - 따라서 pro_trial 체험 사용자도 자동으로 Pro 한도가 적용된다.
 *
 * 이 파일을 참조하는 위치:
 * - ChatPage (AI 채팅 파일 첨부)
 * - LabPage (랩 채점 단일)
 * - ScoringPage (수행평가 채점 단일)
 * - SaenggibuGenerationForm (생기부 생성)
 * - SaenggibuFileUpload, SaenggibuInspector (생기부 검사기)
 * - AssessmentPlanPage (평가계획서)
 * - FileAttachmentZone (재사용 컴포넌트)
 * - Step1_ExcelUpload (일괄 채팅)
 * - HelpCenterPage (도움말 안내 문구)
 *
 * 마지막 업데이트: 2026-06-10 (파일 내용 글자수 Free/Pro 모두 200k로 통일)
 */

// ============================================================================
// 글자수(텍스트 추출 후) 한도
// ============================================================================
// Free/Pro 공통: 200,000자
// 모든 LLM(Gemini 1.5/2.5 1M+, GPT-4o/4.1 128K+) 컨텍스트 윈도우 안에 들어감
export const FREE_FILE_CONTENT_LENGTH = 200000;
export const PRO_FILE_CONTENT_LENGTH = 200000;

// ============================================================================
// 단일 파일 크기(바이트) 한도
// ============================================================================
// Free: 30MB / Pro: 30MB (Cloud Run HTTP/1.x 본문 한도 32MB 안에서 안전)
//
// 정책 결정(2026-06): 파일 크기와 텍스트 글자수 모두 Free/Pro 동일.
export const FREE_FILE_SIZE_BYTES = 30 * 1024 * 1024;
export const PRO_FILE_SIZE_BYTES = 30 * 1024 * 1024;

// ============================================================================
// 공통 보조 한도 (Free/Pro 공통)
// ============================================================================
export const MAX_FILES_DEFAULT = 10;
export const MAX_PDF_PAGES = 30;
export const MAX_PPT_SLIDES = 30;
export const MAX_STORAGE_FILE_SIZE_BYTES = 50 * 1024 * 1024;
// 채팅 첨부(Storage 경로) 한도. 큰 PDF는 쪽 단위로 정리해 질문에 맞는 쪽만 쓴다.
// 백엔드 config/file_limits.py CHAT_STORAGE_* · storage.rules chat_uploads 와 같은 값.
export const CHAT_STORAGE_PDF_MAX_BYTES = 200 * 1024 * 1024;
export const CHAT_STORAGE_DOC_MAX_BYTES = 100 * 1024 * 1024;
// 사전 검사는 파일 본문을 서버로 보내므로(본문 32MB 한도) 이보다 큰 파일은 건너뛴다.
export const PRECHECK_MAX_BYTES = 25 * 1024 * 1024;

export const getChatFileMaxBytes = (file) => (
  /\.pdf$/i.test(file?.name || '') ? CHAT_STORAGE_PDF_MAX_BYTES : CHAT_STORAGE_DOC_MAX_BYTES
);
// 채팅에서 원문 전체를 쪽별로 보관하고 질문에 맞는 부분만 쓰는 형식 — 첨부 합계 한도에서 뺀다.
// 백엔드 services/big_document_context.py FOCUS_EXTENSIONS 와 같은 목록.
export const CHAT_PAGED_EXTENSIONS = [
  '.pdf', '.hwp', '.hwpx', '.pptx', '.docx', '.xlsx', '.xls', '.xlsm', '.csv', '.tsv',
  '.txt', '.md', '.markdown', '.html', '.htm', '.xml', '.json', '.jsonl', '.log',
  '.js', '.jsx', '.ts', '.tsx', '.css', '.scss', '.py', '.java', '.c', '.cpp', '.h',
  '.cs', '.go', '.rb', '.php', '.sql', '.yaml', '.yml', '.tex', '.srt', '.vtt',
  '.ipynb', '.smi', '.rst', '.ini', '.toml', '.r', '.kt', '.swift', '.rs', '.sh',
];

export const isChatPagedFileName = (name = '') => {
  const lower = String(name || '').toLowerCase();
  return CHAT_PAGED_EXTENSIONS.some((ext) => lower.endsWith(ext));
};
// 생기부 교과_고등/동아리/진로 원본은 Storage 경로로 전달되어 생성 단계에서
// student-record 전용 Document/Vision 한도(150p/80슬라이드)를 사용한다.
// 백엔드 main.py MAX_SAENGGIBU_BATCH_PDF_PAGES_FOR_VISION 미러 (10쪽씩 나눠 읽는다).
export const MAX_SAENGGIBU_STORAGE_PDF_PAGES = 150;
export const MAX_SAENGGIBU_STORAGE_PPT_SLIDES = 80;
// 생기부 일괄 생성 1회 학생 수 상한. 백엔드 prompts/saenggibu_category_rules.py
// SAENGGIBU_BATCH_MAX_STUDENTS 미러 (서버가 같은 값으로 400 반환). 5반(150명) 기준.
// 청크를 동시 실행해 150명도 5~6분 안에 끝나게 한 뒤 정한 값.
export const SAENGGIBU_BATCH_MAX_STUDENTS = 150;

/**
 * Pro/Free 한도를 한 번에 가져오는 헬퍼.
 *
 * 사용 예:
 *   const { isPro } = useSubscription();
 *   const { contentLength, fileSize } = getFileLimits(isPro);
 *
 * @param {boolean} isPro - Pro 사용자 여부 (현재 글자수/파일 크기 한도는 공통)
 * @returns {{ contentLength: number, fileSize: number }}
 */
export const getFileLimits = (isPro) => ({
  contentLength: isPro ? PRO_FILE_CONTENT_LENGTH : FREE_FILE_CONTENT_LENGTH,
  fileSize: isPro ? PRO_FILE_SIZE_BYTES : FREE_FILE_SIZE_BYTES,
});

/**
 * 파일 크기 한도를 사람이 읽는 형식으로 변환 (예: '30MB').
 */
export const formatFileSizeLimit = (isPro) =>
  `${Math.round(getFileLimits(isPro).fileSize / (1024 * 1024))}MB`;

/**
 * 글자수 한도를 사람이 읽는 형식으로 변환 (예: '200,000자').
 */
export const formatContentLengthLimit = (isPro) =>
  `${getFileLimits(isPro).contentLength.toLocaleString()}자`;
